import { generateId } from "@/lib/utils";
import { ChatMessage, ChatMessageData, DataResponse } from "@/services/promptEditor.service";
import dayjs from "dayjs";
import { ChatDatum, CompletionDatum } from "../PlaygroundData";
import { createChatMessage } from "./../Chat/ChatMessagesEditor";

export const getLatestDatumTimestamp = (datums: DataResponse[]): number => {
  return Math.max(...datums.map((datum) => dayjs.utc(datum.created_at).unix()));
};

/**
 * Converts a list of `PlaygroundDataResponse`s received from the backend to a
 * list of `ChatDatum`s or `CompletionDatum`s for use in the playground.
 */
export const playgroundDataResponsesToDatums = (
  data: DataResponse[] | null,
): ChatDatum[] | CompletionDatum[] | null => {
  if (data === null) {
    return null;
  }

  const isChat = data.some((datum) => datum.messages !== null);

  if (isChat) {
    return data.map((datum): ChatDatum => {
      const messages = datum.messages || [];

      // Perform the slice to avoid displaying chat template messages twice
      // TODO: Consider improving with `getMessagesAfterTemplate` when we have the full chat template
      const slicedMessages = messages
        .slice(datum.model_config_chat_template_messages_count)
        .map((message) => createChatMessage(message));
      if (datum.output_message) {
        slicedMessages.push(createChatMessage({ ...datum.output_message, logId: datum.id }));
      }
      return {
        type: "chat",
        id: datum.id || generateId("datum"),
        inputs: datum.inputs || {},
        messages: slicedMessages,
        status: "not_run",
        streamSource: null,
        error: null,
        tool_results: datum.tool_results || [],
        session_reference_id: generateId("editor_session"),
        outputMessages: [], // TODO(oleh): Populate this?
        continueMessages: [],
      };
    });
  } else {
    // All items are treated as CompletionDatum
    return data.map((datum): CompletionDatum => {
      return {
        type: "completion",
        id: datum.id || generateId("datum"),
        logId: datum.id,
        inputs: datum.inputs || {},
        output: datum.output,
        raw_output: datum.raw_output,
        status: "not_run",
        streamSource: null,
        error: null,
        tool_results: datum.tool_results || [],
      };
    });
  }
};

/**
 * If the chat template is present, we need to remove it from the messages
 * to avoid duplication.
 * We could do `messages.slice(chatTemplate.length)`, but we need to handle the case
 * where the Log's messages list does not contain the chat template (due to user misunderstanding).
 * In this case, we should just return the messages as is.
 */
export const getMessagesAfterTemplate = (
  messages: ChatMessage[] | null,
  chatTemplate: ChatMessageData[] | null,
): ChatMessage[] | null => {
  if (messages === null) return null;
  if (chatTemplate === null || chatTemplate.length === 0) return messages;

  // If the messages list contains the chat template, we remove it.
  if (
    chatTemplate.every((m, i) => {
      const message = messages[i];
      if (!message) return false;
      if (message.role !== m.role) return false;
      if (message.content === m.content) return true;
      // If the message contains inputs, we can't compare the content directly.
      // We could replace the inputs with a wildcard and try and match it that way,
      // but it's complicated.
      // Hence we just assume they're the same for now. This is an improvement on current behaviour.
      return true;
    })
  ) {
    return messages.slice(chatTemplate.length);
  }
  // Otherwise, we return the messages as is.
  return messages;
};
